import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  IconButton,
  Modal,
  CircularProgress,
  Divider,
  Paper,
  alpha,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  AccessTime as ClockIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon,
  ChatBubbleOutline as MessageIcon,
  Send as SendIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import moment from "moment";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const GOLD = "#D4AF37";

const Feed = () => {
  const { id } = useParams();
  const theme = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("jwttoken");
  const [likedPosts, setLikedPosts] = useState({});
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [postContent, setPostContent] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const backendURL = "https://tdtlworld.com/dss-kaas-backend";
  const [searchFeed, setSearchFeed] = useState("");
  const [showComments, setShowComments] = useState({});
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchPosts();
  }, [id]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${backendURL}/socialapp/groups/${id}/posts/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPosts(response.data);
      const userId = JSON.parse(atob(token.split(".")[1])).user_id;
      setLikedPosts(
        response.data.reduce((acc, post) => {
          acc[post.id] = post.likers.includes(userId);
          return acc;
        }, {})
      );
    } catch (error) {
      console.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    if (comments[postId]) return;
    try {
      const response = await axios.get(
        `${backendURL}/socialapp/groups/${id}/posts/${postId}/comments/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setComments((prev) => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handleLikeToggle = async (postId, liked) => {
    setLikedPosts((prev) => ({ ...prev, [postId]: !liked }));
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId ? { ...post, likes_count: liked ? post.likes_count - 1 : post.likes_count + 1 } : post
      )
    );
  
    try {
      await axios.post(`${backendURL}/socialapp/groups/${id}/posts/${postId}/${liked ? "unlike" : "like"}/`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error toggling like:", error);
      fetchPosts(); 
    }
  };
  
  const handleCommentChange = (postId, value) => {
    setNewComments((prev) => ({ ...prev, [postId]: value }));
  };

  const handlePostComment = async (postId) => {
    if (!newComments[postId]?.trim()) return;

    try {
      await axios.post(
        `${backendURL}/socialapp/groups/${id}/posts/${postId}/comments/`,
        { content: newComments[postId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewComments((prev) => ({ ...prev, [postId]: "" }));
      setComments((prev) => ({ ...prev, [postId]: undefined }));
      fetchComments(postId);
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePostSubmit = async () => {
    if (!postContent.trim()) {
      Swal.fire("Warning!", "Post content cannot be empty.", "warning");
      return;
    }
    setPosting(true);

    const formData = new FormData();
    formData.append("content", postContent);
    if (attachedImage) {
      formData.append("image", attachedImage);
    }

    try {
      await axios.post(
        `${backendURL}/socialapp/groups/${id}/posts/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setPostContent("");
      setAttachedImage(null);
      setImagePreview(null);
      setShowModal(false);
      fetchPosts();
    } catch (error) {
      console.error("Error adding post:", error);
    } finally {
      setPosting(false);
    }
  };

  const handleSearch = (query) => {
    setSearchFeed(query);
  };

  const filteredPosts = posts.filter((post) =>
    post.content.toLowerCase().includes(searchFeed.toLowerCase()) ||
    `${post.user.first_name} ${post.user.last_name}`.toLowerCase().includes(searchFeed.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
      {/* Create Post Entry */}
      <Card 
        onClick={() => setShowModal(true)}
        sx={{ 
          mb: 4, 
          borderRadius: 4, 
          cursor: 'pointer',
          border: `1px solid ${alpha(GOLD, 0.1)}`,
          '&:hover': { bgcolor: alpha(GOLD, 0.02) }
        }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: alpha(GOLD, 0.2), color: GOLD }}>
            <PersonIcon />
          </Avatar>
          <Box 
            sx={{ 
              flex: 1, 
              bgcolor: 'background.default', 
              borderRadius: '50px', 
              px: 3, 
              py: 1.5,
              color: 'text.secondary',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            What's on your mind?
          </Box>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search in feed..."
        value={searchFeed}
        onChange={(e) => handleSearch(e.target.value)}
        sx={{ mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: GOLD }} />
            </InputAdornment>
          ),
          sx: { borderRadius: '50px' }
        }}
      />

      {/* Post Submission Modal */}
      <Modal 
        open={showModal} 
        onClose={() => setShowModal(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      >
        <Paper 
          sx={{ 
            width: '100%', 
            maxWidth: 500, 
            borderRadius: 4, 
            p: 3, 
            outline: 'none',
            position: 'relative' 
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
              Create a post
            </Typography>
            <IconButton onClick={() => setShowModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="What's on your mind?"
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            sx={{ mb: 3 }}
          />

          {imagePreview && (
            <Box sx={{ position: 'relative', mb: 3, borderRadius: 2, overflow: 'hidden' }}>
              <img src={imagePreview} alt="Preview" style={{ width: '100%', height: 'auto', display: 'block' }} />
              <IconButton 
                onClick={() => { setAttachedImage(null); setImagePreview(null); }}
                sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              component="label"
              startIcon={<CameraIcon />}
              sx={{ color: GOLD, textTransform: 'none' }}
            >
              Attach Photo
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
            </Button>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="text" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handlePostSubmit}
                disabled={posting}
                sx={{ 
                  bgcolor: GOLD, 
                  px: 4, 
                  borderRadius: '50px',
                  '&:hover': { bgcolor: '#B8860B' } 
                }}
              >
                {posting ? <CircularProgress size={24} color="inherit" /> : "Post"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>

      {/* Feed Content */}
      <AnimatePresence>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        ) : (
          filteredPosts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card sx={{ mb: 4, borderRadius: 4, border: `1px solid ${alpha(GOLD, 0.1)}` }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar 
                      src={post.user.profile_pic ? `${backendURL}${post.user.profile_pic}` : undefined}
                      sx={{ width: 48, height: 48, border: `2px solid ${alpha(GOLD, 0.3)}` }}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {post.user.first_name} {post.user.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ClockIcon fontSize="inherit" />
                        {moment(post.created_at).fromNow()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Body */}
                  <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {post.content}
                  </Typography>

                  {post.image && (
                    <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                      <img 
                        src={`${backendURL}/lms/media/group_posts/${post.image.split("/db-shiksha/kaas/").pop()}`}
                        alt="Post content"
                        style={{ width: '100%', maxHeight: 500, objectFit: 'cover' }}
                      />
                    </Box>
                  )}

                  <Divider sx={{ my: 2, opacity: 0.5 }} />

                  {/* Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Box 
                      onClick={() => handleLikeToggle(post.id, likedPosts[post.id])}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        cursor: 'pointer',
                        color: likedPosts[post.id] ? '#f44336' : 'text.secondary',
                        '&:hover': { color: '#f44336' }
                      }}
                    >
                      {likedPosts[post.id] ? <HeartIcon /> : <HeartBorderIcon />}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{post.likes_count} Likes</Typography>
                    </Box>
                    
                    <Box 
                      onClick={() => {
                        setShowComments(prev => ({ ...prev, [post.id]: !prev[post.id] }));
                        if (!comments[post.id]) fetchComments(post.id);
                      }}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        cursor: 'pointer',
                        color: showComments[post.id] ? GOLD : 'text.secondary',
                        '&:hover': { color: GOLD }
                      }}
                    >
                      <MessageIcon />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Comments</Typography>
                    </Box>
                  </Box>

                  {/* Comments Section */}
                  <AnimatePresence>
                    {showComments[post.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}` }}>
                          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(GOLD, 0.1), color: GOLD }}>?</Avatar>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Write a comment..."
                              value={newComments[post.id] || ""}
                              onChange={(e) => handleCommentChange(post.id, e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handlePostComment(post.id)}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton onClick={() => handlePostComment(post.id)} size="small" sx={{ color: GOLD }}>
                                      <SendIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                                sx: { borderRadius: '20px' }
                              }}
                            />
                          </Box>

                          {comments[post.id]?.map((comment) => (
                            <Box key={comment.id} sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                              <Avatar 
                                src={comment.user.profile_pic ? `${backendURL}${comment.user.profile_pic}` : undefined}
                                sx={{ width: 32, height: 32 }}
                              />
                              <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: '0 12px 12px 12px', flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                  {comment.user.first_name} {comment.user.last_name}
                                </Typography>
                                <Typography variant="body2" sx={{ my: 0.5 }}>{comment.content}</Typography>
                                <Typography variant="caption" color="text.secondary">{moment(comment.created_at).fromNow()}</Typography>
                              </Box>
                            </Box>
                          ))}
                          {comments[post.id]?.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                              No comments yet. Be the first to join the conversation!
                            </Typography>
                          )}
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Feed;
